import { Dropdown } from 'components/form/inputs/Dropdown'
import { DEFAULT_EDGE_STROKE_WIDTH } from 'features/processFlowDiagrams/components/edges/SolidLine'
import {
  FormSection,
  MetadataInput,
  PanelContainer,
  PanelHeader,
  SectionTitle,
} from 'features/processFlowDiagrams/components/nodes/propertyPanel/StylesAndHelper'
import { ProcessFlowDiagramBuilderContext } from 'features/processFlowDiagrams/contexts/ProcessFlowDiagramBuilderContext'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { PfdEdge } from 'types'

const EdgePanelContainer = styled(PanelContainer)`
  width: 18rem;
`

const DEFAULT_HANDLE_OPTIONS = [
  { value: 'top-source', label: 'Top' },
  { value: 'bottom-source', label: 'Bottom' },
  { value: 'left-source', label: 'Left' },
  { value: 'right-source', label: 'Right' },
]

interface EdgePropertyPanelProps {
  selectedEdge: PfdEdge
}

export const EdgePropertyPanel: React.FC<EdgePropertyPanelProps> = ({
  selectedEdge,
}) => {
  const { setPfd, setSelectedEdge } = useContext(
    ProcessFlowDiagramBuilderContext
  )

  const updateEdgeData = useCallback(
    (patch: Partial<NonNullable<PfdEdge['data']>>) => {
      const updatedData = { ...selectedEdge.data, ...patch }
      setPfd((prev) => ({
        ...prev,
        edges: prev.edges.map((e) =>
          e.id === selectedEdge.id ? { ...e, data: updatedData } : e
        ),
      }))
      setSelectedEdge((prev) =>
        prev?.id === selectedEdge.id ? { ...prev, data: updatedData } : prev
      )
    },
    [selectedEdge, setPfd, setSelectedEdge]
  )

  const updateEdgeProps = useCallback(
    (patch: Partial<Pick<PfdEdge, 'sourceHandle' | 'targetHandle'>>) => {
      setPfd((prev) => ({
        ...prev,
        edges: prev.edges.map((e) =>
          e.id === selectedEdge.id ? { ...e, ...patch } : e
        ),
      }))
      setSelectedEdge((prev) =>
        prev?.id === selectedEdge.id ? { ...prev, ...patch } : prev
      )
    },
    [selectedEdge, setPfd, setSelectedEdge]
  )

  const handleLabelChange = useCallback(
    (value: string) => updateEdgeData({ label: value || undefined }),
    [updateEdgeData]
  )

  const handleStrokeWidthChange = useCallback(
    (value: string) => {
      const parsed = parseFloat(value)
      if (!Number.isNaN(parsed) && parsed > 0) {
        updateEdgeData({ strokeWidth: parsed })
      }
    },
    [updateEdgeData]
  )

  const edgeTitle = `${selectedEdge.source} → ${selectedEdge.target}`

  return (
    <EdgePanelContainer>
      <PanelHeader>{edgeTitle}</PanelHeader>
      <FormSection>
        <SectionTitle>Label</SectionTitle>
        <MetadataInput
          value={selectedEdge.data?.label ?? ''}
          onChange={(e) => handleLabelChange(e.target.value)}
          placeholder='Edge label (shown on line)'
        />
      </FormSection>
      <FormSection>
        <SectionTitle>Size (px)</SectionTitle>
        <MetadataInput
          type='number'
          min={1}
          max={20}
          step={1}
          value={selectedEdge.data?.strokeWidth ?? DEFAULT_EDGE_STROKE_WIDTH}
          onChange={(e) => handleStrokeWidthChange(e.target.value)}
        />
      </FormSection>
      <FormSection>
        <SectionTitle>Source Handle</SectionTitle>
        <Dropdown
          key={`${selectedEdge.id}-source-handle`}
          options={DEFAULT_HANDLE_OPTIONS}
          defaultValue={selectedEdge.sourceHandle ?? ''}
          onValueChange={(value) => updateEdgeProps({ sourceHandle: value })}
          placeholder='Select handle'
        />
      </FormSection>
      <FormSection>
        <SectionTitle>Target Handle</SectionTitle>
        <Dropdown
          key={`${selectedEdge.id}-target-handle`}
          options={DEFAULT_HANDLE_OPTIONS}
          defaultValue={selectedEdge.targetHandle ?? ''}
          onValueChange={(value) => updateEdgeProps({ targetHandle: value })}
          placeholder='Select handle'
        />
      </FormSection>
    </EdgePanelContainer>
  )
}
